import { useEffect } from 'react';
import intentPages from '../data/seoPages.json';

const SITE_ORIGIN = 'https://bikepackinglive.com';
const DEFAULT_TITLE = 'Bikepacking Live | Plan Trips & Share Your Ride Live';
const DEFAULT_DESCRIPTION =
  'Plan multi-day bikepacking trips, organize stages, and record rides privately for free. Share one live tracking link with friends and family when you ride.';

type PageMetadata = {
  title: string;
  description: string;
  canonicalPath: string;
  index: boolean;
};

const routeImageByPath: Record<string, string> = {
  '/': 'via-claudia-stages',
  '/bikepacking-trip-planner': 'via-claudia-stages',
  '/live-bikepacking-tracker': 'berlin-potsdam-live',
  '/share-bikepacking-trip': 'vienna-bratislava-share',
  '/offline-bikepacking-tracker': 'black-forest-offline',
  '/bikepacking-gpx-route-planner': 'girona-olot-gpx',
  '/bikepacking-distance-per-day': 'salzburg-bad-ischl-distance',
  '/weekend-bikepacking-trip-planner': 'potsdam-wittenberg-weekend',
  '/bikepacking-app': 'ljubljana-bled-app',
  '/route-methodology': 'via-claudia-stages',
};

const publicPages: Record<string, PageMetadata> = {
  '/': {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    canonicalPath: '/',
    index: true,
  },
  '/privacy': {
    title: 'Privacy Policy | Bikepacking Live',
    description:
      'How Bikepacking Live processes and protects account, trip, location, and subscription data.',
    canonicalPath: '/privacy',
    index: true,
  },
  '/terms': {
    title: 'Terms of Service | Bikepacking Live',
    description:
      'The terms for planning, recording, and sharing bikepacking trips with Bikepacking Live.',
    canonicalPath: '/terms',
    index: true,
  },
  '/imprint': {
    title: 'Imprint | Bikepacking Live',
    description: 'Legal and operator information for Bikepacking Live.',
    canonicalPath: '/imprint',
    index: true,
  },
  '/delete-account': {
    title: 'Delete Your Account | Bikepacking Live',
    description: 'Request deletion of your Bikepacking Live account and associated trip data.',
    canonicalPath: '/delete-account',
    index: true,
  },
  '/route-methodology': {
    title: 'Route Methodology | Bikepacking Live',
    description:
      'How Bikepacking Live generated, simplified, checked, and presented the real bicycle routes used in its planning and tracking examples.',
    canonicalPath: '/route-methodology/',
    index: true,
  },
};

for (const page of intentPages) {
  publicPages[`/${page.slug}`] = {
    title: page.title,
    description: page.description,
    canonicalPath: `/${page.slug}/`,
    index: true,
  };
}

const privatePageTitles: Record<string, string> = {
  '/signin': 'Sign In | Bikepacking Live',
  '/start': 'Start Planning | Bikepacking Live',
};

function setNamedMeta(name: string, content: string) {
  let element = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.name = name;
    document.head.appendChild(element);
  }
  element.content = content;
}

function setPropertyMeta(property: string, content: string) {
  const element = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (element) element.content = content;
}

export function Seo({ pathname }: { pathname: string }) {
  useEffect(() => {
    const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    const metadata = publicPages[normalizedPath] ?? {
      title: privatePageTitles[normalizedPath] ?? 'Bikepacking Live',
      description: DEFAULT_DESCRIPTION,
      canonicalPath: '/',
      index: false,
    };
    const canonicalUrl = `${SITE_ORIGIN}${metadata.canonicalPath}`;
    const routeImageId = routeImageByPath[normalizedPath] ?? 'via-claudia-stages';
    const imageUrl = `${SITE_ORIGIN}/route-images/${routeImageId}.png`;

    document.title = metadata.title;
    setNamedMeta('description', metadata.description);
    setNamedMeta(
      'robots',
      metadata.index
        ? 'index, follow, max-image-preview:large, max-snippet:-1'
        : 'noindex, nofollow, noarchive',
    );
    setNamedMeta('twitter:title', metadata.title);
    setNamedMeta('twitter:description', metadata.description);
    setNamedMeta('twitter:card', 'summary_large_image');
    setNamedMeta('twitter:image', imageUrl);
    setPropertyMeta('og:title', metadata.title);
    setPropertyMeta('og:description', metadata.description);
    setPropertyMeta('og:url', canonicalUrl);
    setPropertyMeta('og:image', imageUrl);
    setPropertyMeta('og:image:width', '1200');
    setPropertyMeta('og:image:height', '630');
    setPropertyMeta('og:image:alt', `${metadata.title} route example`);

    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = canonicalUrl;
  }, [pathname]);

  return null;
}
